import * as React from 'react';
import { useQuery } from '@apollo/client';
import * as ShowOrganization from '../../core/gql/queries/show-organizations';
import { ChurnZeroService } from 'frontend-toolkit';
import { AuthContext } from 'auth/dist/contexts';
import { Role, formatRoleForDisplay, getRole, isOneOf } from 'auth/dist/roles';
import { CHURNZERO_APP_KEY, CHURNZERO_URL } from '../../core/constants';

const shouldSendToChurnZero = (organization) => organization && !organization?.isTrial && !organization?.isTest;

export const trackChurnZeroEvent = (message, organization) => {
  if (shouldSendToChurnZero(organization)) {
    ChurnZeroService.trackChurnZeroEvent(message);
  }
};

export const stopChurnZero = () => {
  ChurnZeroService.stop();
};

export const setChurnZeroContact = (email, organization) => {
  ChurnZeroService.setChurnZeroContact(organization.churnZeroId, email);
};

const useChurnZero = ({ organizationId }) => {
  const [currentChurnZeroId, setChurnZeroId] = React.useState(null);
  const context = React.useContext(AuthContext);
  const queryOrganization = useQuery(ShowOrganization.BASIC_ORG_QUERY, {
    variables: { organizationId },
  });

  const currentOrg = queryOrganization?.data?.showOrganization;

  React.useEffect(() => {
    if (
      shouldSendToChurnZero(currentOrg) &&
      currentOrg?.churnZeroId !== currentChurnZeroId
    ) {
      setChurnZeroId(currentOrg?.churnZeroId);
    }
  }, [queryOrganization.data, currentChurnZeroId]);

  React.useEffect(() => {
    if (!currentChurnZeroId || !currentOrg) {
      return;
    }

    if (
      !shouldSendToChurnZero(currentOrg) ||
      context.user.email.includes('@teamsnap.com')
    ) {
      return;
    }

    if (
      isOneOf(
        `${organizationId}`,
        context.user,
        Role.Owner,
        Role.OrgAdmin,
        Role.ProgramAdmin,
        Role.ProgramCoordinator
      )
    ) {
      ChurnZeroService.setChurnZeroContact(
        currentChurnZeroId,
        context.user.email,
        context.user.personUuid,
        context.user.firstName,
        context.user.lastName,
        getRole(context.user, organizationId)
      );
    }
  }, [context.user, currentChurnZeroId, currentOrg, organizationId]);

  React.useEffect(() => {
    if (shouldSendToChurnZero(currentOrg)) {
      ChurnZeroService.setChurnZeroAppKey(CHURNZERO_APP_KEY);
    } else {
      ChurnZeroService.stop();
    }
  }, [currentOrg]);

  const setChurnZeroAttribute = (firstName, lastName, email, role) => {
    ChurnZeroService.setChurnZeroAttribute(CHURNZERO_URL, {
      appKey: CHURNZERO_APP_KEY,
      contactId: currentChurnZeroId,
      contactEmail: email,
      firstName: firstName,
      lastName: lastName,
      role: !role ? 'Removed from Org' : formatRoleForDisplay(role),
    });
  };

  return { setChurnZeroAttribute };
};

export default useChurnZero;
